import type { Requester } from '@labeling/globals';
import { useTranslation } from 'react-i18next';
import { roleLabel } from './role-utils';
import { useRoleAssumption } from './use-role-assumption';

type Props = {
  /**
   * 'dark' (default) — white text on the dark navbar background.
   * 'light' — slate text for the light mobile drawer background.
   */
  variant?: 'dark' | 'light';
  className?: string;
};

/**
 * Renders a "Viewing as <role>" select when the logged-in user has more than
 * one role available (e.g. admin who also has app-user roles). Selecting a
 * role overrides the effective Requester in the Zustand store so the user can
 * experience the UI from that role's perspective.
 *
 * This is a purely client-side override — the Bearer token sent to the API is
 * unchanged, so the backend always sees the user's real identity.
 */
export const RoleAssumptionDropdown = ({ variant = 'dark', className = '' }: Props) => {
  const { t } = useTranslation();
  const { hasMultipleRoles, sortedRoles, currentRoleType, selectRole } = useRoleAssumption();

  if (!hasMultipleRoles) return null;

  const isDark = variant === 'dark';

  return (
    <div className={`flex items-center gap-200 ${className}`}>
      <span
        className={`text-body-md font-medium shrink-0 ${isDark ? 'text-core-white' : 'text-core-slate-1300'}`}
      >
        {t('navbar.display.role')}
      </span>
      <select
        aria-label={t('navbar.display.role')}
        value={currentRoleType}
        onChange={(e) => selectRole(e.target.value as Requester['type'])}
        className={
          isDark
            ? 'flex-1 text-body-sm bg-core-blue-800 text-core-white border border-core-white/30 rounded-100 px-100 py-50 cursor-pointer hover:border-core-white/60 focus:outline-none focus:ring-1 focus:ring-core-white/50'
            : 'flex-1 text-body-sm bg-core-white text-core-slate-1300 border border-core-slate-300 rounded-100 px-100 py-50 cursor-pointer hover:border-core-slate-500 focus:outline-none focus:ring-1 focus:ring-core-blue-500'
        }
      >
        {sortedRoles.map((role) => (
          <option key={role.type} value={role.type}>
            {roleLabel(role.type, t)}
          </option>
        ))}
      </select>
    </div>
  );
};
